import { useState } from "react";
import { useAppDispatch } from "@/store";
import { modalDispatcher } from "@/store/slices/modal-slice";
import { snackbarDispatcher } from "@/store/slices/snackbar-slice";
import {
  useDuplicateWidgetMutation,
  useMoveWidgetMutation,
} from "@/store/api/widget-api";

interface UseCopyMoveWidgetArgs {
  mode: "copy" | "move";
  dashboardId: string;
  sourceTabId: string;
  widgetId: string;
  onViewTab: (
    targetTabId: string,
    targetTabLabel: string,
    widgetId: string,
  ) => void;
}

export function useCopyMoveWidget({
  mode,
  dashboardId,
  sourceTabId,
  widgetId,
  onViewTab,
}: UseCopyMoveWidgetArgs) {
  const dispatch = useAppDispatch();
  const { closeTopModal } = modalDispatcher(dispatch);
  const { showSuccessSnackbar } = snackbarDispatcher(dispatch);

  const [selectedTabId, setSelectedTabId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [moveWidget, { isLoading: isMoving }] = useMoveWidgetMutation();
  const [duplicateWidget, { isLoading: isDuplicating }] =
    useDuplicateWidgetMutation();

  const isSubmitting = isMoving || isDuplicating;

  const handleConfirm = async (destinationLabel: string) => {
    if (!selectedTabId) return;
    const targetTabId = selectedTabId;
    setError(null);

    try {
      if (mode === "move") {
        await moveWidget({
          dashboardId,
          tabId: sourceTabId,
          widgetId,
          targetTabId,
        }).unwrap();
      } else {
        await duplicateWidget({
          dashboardId,
          tabId: sourceTabId,
          widgetId,
          targetTabId,
        }).unwrap();
      }

      closeTopModal();
      showSuccessSnackbar({
        message: `Widget ${mode === "copy" ? "copied" : "moved"} to ${destinationLabel}.`,
        action: {
          label: "View tab",
          onClick: () => onViewTab(targetTabId, destinationLabel, widgetId),
        },
      });
    } catch {
      setError(
        `${mode === "copy" ? "Copy" : "Move"} failed. Please try again.`,
      );
    }
  };

  return {
    selectedTabId,
    setSelectedTabId,
    isSubmitting,
    error,
    handleConfirm,
  };
}
